"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import Script from "next/script";
import { usePathname } from "next/navigation";
import { CookiesModal } from "@/components/Cookies/CookiesModal";

import { cookieCategories } from "@/components/Cookies/cookiesCategory";
import { isNoTrackingRoute } from "@/constants/noTrackingRoutes";
/* ───── Context + Hook ───── */
const ConsentContext = createContext(null);
export const useConsent = () => useContext(ConsentContext);

/* ───── Provider ───── */
export default function CookieConsentProvider({ children }) {
  const pathname = usePathname();
  const blockTracking = isNoTrackingRoute(pathname);

  /* ───── Zustände ───── */
  const [isEssentialChecked] = useState(true); // immer true

  const [isAnalyticalChecked, setIsAnalyticalChecked] = useState(
    !!Cookies.get("analytical")
  );
  const [isMarketingChecked, setIsMarketingChecked] = useState(
    !!Cookies.get("marketing")
  );

  const [bannerOpen, setBannerOpen] = useState(false);
  useEffect(() => {
    if (blockTracking) {
      setBannerOpen(false);
      return;
    }
    const accepted = localStorage.getItem("cookieConsentAcceptedPro");
    setBannerOpen(accepted !== "true");
  }, [blockTracking]);

  /* ───── Helper ───── */
  const persistCookie = (key, value) => {
    if (value) {
      Cookies.set(key, JSON.stringify(cookieCategories[key]), { expires: 365 });
    } else {
      Cookies.remove(key);
    }
  };

  /* ───── Skripte dynamisch nachladen ───── */
  useEffect(() => {
    if (blockTracking) return;
    if (!isAnalyticalChecked || window.clarity) return;

    (function (c, l, a, r, i, t, y) {
      if (c.clarity) return;
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", "r78cayvlow");
  }, [isAnalyticalChecked, blockTracking]);

  useEffect(() => {
    if (blockTracking) return;
    if (!isMarketingChecked || window.fbq) return;

    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );
    window.fbq("init", "645534931725309");
    window.fbq("track", "PageView");
  }, [isMarketingChecked, blockTracking]);

  useEffect(() => {
    if (!blockTracking || typeof window === "undefined") return;

    try {
      if (window.fbq) window.fbq("consent", "revoke");
    } catch {}

    try {
      if (window.clarity) window.clarity("consent", false);
    } catch {}
  }, [blockTracking]);

  const openCookieModal = () => setBannerOpen(true); // 👈 NEU

  /* ───── Context-Werte ───── */
  const ctx = {
    isEssentialChecked,
    setIsEssentialChecked: () => {}, // noop

    isAnalyticalChecked,
    setIsAnalyticalChecked: (v) => {
      setIsAnalyticalChecked(v);
      persistCookie("analytical", v);
    },

    isMarketingChecked,
    setIsMarketingChecked: (v) => {
      setIsMarketingChecked(v);
      persistCookie("marketing", v);
    },
    openCookieModal,
  };

  return (
    <ConsentContext.Provider value={ctx}>
      {!blockTracking && (
        <CookiesModal isOpen={bannerOpen} setIsOpen={setBannerOpen} />
      )}

      {!blockTracking && isAnalyticalChecked && (
        <Script
          id="clarity-fallback"
          strategy="afterInteractive"
          src="https://www.clarity.ms/tag/r78cayvlow"
        />
      )}
      {!blockTracking && isMarketingChecked && (
        <Script
          id="fb-pixel-fallback"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `/* gleiches Snippet wie oben */`,
          }}
        />
      )}

      {children}
    </ConsentContext.Provider>
  );
}
